import { LitElement, html, nothing, type TemplateResult, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { repeat } from 'lit/directives/repeat.js';
import styles from './styles.js';
import type { MenuItem, MenuItemCommandEvent, MenuItemSelectEventDetail } from '../types.js';
import { emit, computeCoords, clampToViewport } from '../utils/dom.utils.js';

/**
 * uwc-menubar
 *
 * A full-featured horizontal navigation menubar with nested submenu support,
 * desktop hover / keyboard navigation, and a mobile hamburger panel.
 *
 * Follows the WAI-ARIA Menubar pattern.
 *
 * @attr items        - MenuItem[] — the navigation model.
 * @attr aria-label   - Accessible label for the menubar region. Default: 'Navigation'.
 * @attr menu-align   - Alignment of the nav list items: `'start'` (default), `'center'`, or `'end'`.
 * @fires uwc-item-select — detail: { item: MenuItem, originalEvent: Event }
 *
 * @slot start — Logo / brand area rendered at the leading edge.
 * @slot end   — Trailing area (search, avatar, actions).
 *
 * @cssprop --uwc-menubar-list-justify - `justify-content` for the nav list. Overridden by `menu-align`. Default: `flex-start`.
 * @cssprop --uwc-menubar-bg
 * @cssprop --uwc-menubar-color
 * @cssprop --uwc-menubar-border
 * @cssprop --uwc-menubar-border-radius
 * @cssprop --uwc-menubar-shadow
 * @cssprop --uwc-menubar-padding
 * @cssprop --uwc-menubar-gap
 * @cssprop --uwc-menubar-height
 * @cssprop --uwc-menubar-item-padding
 * @cssprop --uwc-menubar-item-radius
 * @cssprop --uwc-menubar-item-hover-bg
 * @cssprop --uwc-menubar-item-active-bg
 * @cssprop --uwc-menubar-item-font-size
 * @cssprop --uwc-menubar-item-font-weight
 * @cssprop --uwc-menubar-panel-bg
 * @cssprop --uwc-menubar-panel-color
 * @cssprop --uwc-menubar-panel-border
 * @cssprop --uwc-menubar-panel-radius
 * @cssprop --uwc-menubar-panel-shadow
 * @cssprop --uwc-menubar-panel-padding
 * @cssprop --uwc-menubar-panel-min-width
 * @cssprop --uwc-menubar-panel-item-padding
 * @cssprop --uwc-menubar-panel-item-hover-bg
 * @cssprop --uwc-menubar-panel-danger-color
 * @cssprop --uwc-menubar-panel-separator-color
 * @cssprop --uwc-menubar-z
 */
export type MenubarMenuAlign = 'start' | 'center' | 'end';

export class UwcMenubar extends LitElement {

  // ── Public properties ─────────────────────────────────────────────

  @property({ type: Array })
  items: MenuItem[] = [];

  @property({ type: String, attribute: 'aria-label' })
  ariaLabel = 'Navigation';

  /** Alignment of nav list items within the bar. */
  @property({ reflect: true, attribute: 'menu-align' })
  menuAlign: MenubarMenuAlign = 'start';

  /** When true, hides the horizontal nav list. */
  @property({ type: Boolean, reflect: true })
  compact = false;

  // ── Internal state ────────────────────────────────────────────────

  /** Dot-separated index path(s) of open panels, e.g. Set { "1", "1.2" } */
  @state() private _openPaths: Set<string> = new Set();

  @state() private _mobileOpen = false;

  @state() private _focusedTopIndex: number | null = null;

  /** Index of the persistently selected top-level item (set on leaf click). */
  @state() private _selectedTopIndex: number | null = null;

  /** Path of the persistently selected panel item (set on leaf click). */
  @state() private _selectedPath: string | null = null;

  // ── Hover timer storage ───────────────────────────────────────────

  private _hoverTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  // Stores computed coords for every panel (top-level + nested), keyed by path
  private _panelCoords   = new Map<string, { top: number; left: number }>();
  // Stores the trigger element for every panel so we can reposition on scroll
  private _panelTriggers = new Map<string, HTMLElement>();

  // True when at least one top-level panel is open (drives hover-switch behaviour)
  private get _menuActive(): boolean {
    for (const p of this._openPaths) {
      if (!p.includes('.')) return true;
    }
    return false;
  }

  // ── Scroll / resize reposition ────────────────────────────────────

  private readonly _onScrollOrResize = (): void => {
    if (this._openPaths.size > 0 || this._mobileOpen) this._repositionOpenPanels();
  };

  private _repositionOpenPanels(): void {
    for (const path of this._openPaths) {
      const panel = this.shadowRoot?.querySelector<HTMLElement>(`[data-panel-popover="${path}"]`);
      if (!panel) continue;
      const triggerEl = this._panelTriggers.get(path);
      if (!triggerEl) continue;
      const tRect    = triggerEl.getBoundingClientRect();
      const pRect    = panel.getBoundingClientRect();
      const isNested = path.includes('.');
      // Mirror PlacementController.position(): computeCoords → clampToViewport
      const placement = isNested
        ? ((window.innerWidth - tRect.right - 4) >= pRect.width ? 'right-start' : 'left-start')
        : 'bottom-start';
      const raw    = computeCoords(tRect, pRect, placement, 4);
      const coords = clampToViewport(raw, pRect);
      panel.style.top  = `${coords.top}px`;
      panel.style.left = `${coords.left}px`;
    }
    if (this._mobileOpen) {
      const mobilePanel = this.shadowRoot?.querySelector<HTMLElement>('#uwc-mbar-mobile-panel');
      if (mobilePanel) this._positionMobilePanel(mobilePanel);
    }
  }

  private _positionMobilePanel(mobilePanel: HTMLElement): void {
    const r = this.getBoundingClientRect();
    mobilePanel.style.top   = `${r.bottom + 4}px`;
    mobilePanel.style.left  = '8px';
    mobilePanel.style.width = `${window.innerWidth - 16}px`;
  }

  // ── Outside-click handler ref ─────────────────────────────────────

  private _onDocClick = (e: MouseEvent) => {
    if (!this.shadowRoot) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this._closeAll();
    }
  };

  // ── Lifecycle ─────────────────────────────────────────────────────

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this._onDocClick);
    window.addEventListener('scroll', this._onScrollOrResize, { passive: true, capture: true });
    window.addEventListener('resize', this._onScrollOrResize, { passive: true });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDocClick);
    window.removeEventListener('scroll', this._onScrollOrResize, { capture: true });
    window.removeEventListener('resize', this._onScrollOrResize);
    this._clearAllTimers();
  }

  // ── Helpers ───────────────────────────────────────────────────────

  private _clearAllTimers(): void {
    this._hoverTimers.forEach(id => clearTimeout(id));
    this._hoverTimers.clear();
  }

  private _closeAll(): void {
    this._openPaths = new Set();
    this._mobileOpen = false;
    this._focusedTopIndex = null;
  }

  private _isOpen(path: string): boolean {
    return this._openPaths.has(path);
  }

  private _openPath(path: string): void {
    const next = new Set(this._openPaths);
    if (path.startsWith('mobile-')) {
      // Mobile accordion: close siblings at same depth and deeper, keep ancestors
      const depth = parseInt(path.split('-')[1], 10);
      for (const p of next) {
        if (!p.startsWith('mobile-')) continue;
        const pDepth = parseInt(p.split('-')[1], 10);
        if (pDepth >= depth && p !== path) next.delete(p);
      }
    } else {
      // Desktop: close sibling top-level panels only; never touch mobile paths
      const topSegment = path.split('.')[0];
      for (const p of next) {
        if (!p.startsWith('mobile-') && p.split('.')[0] !== topSegment) next.delete(p);
      }
    }
    next.add(path);
    this._openPaths = next;
  }

  private _closePath(path: string): void {
    const next = new Set(this._openPaths);
    // Close the path and any nested children of it
    for (const p of next) {
      if (p === path || p.startsWith(path + '.')) {
        next.delete(p);
      }
    }
    this._openPaths = next;
  }

  // ── Hover logic (desktop) ─────────────────────────────────────────

  private _onItemMouseEnter(path: string, itemEl: HTMLElement): void {
    const isTopLevel = !path.includes('.');
    // Top-level panels only open on hover when the menu is already active (click opened first)
    if (isTopLevel && !this._menuActive) return;

    const closeKey = path + '-close';
    const pendingClose = this._hoverTimers.get(closeKey);
    if (pendingClose !== undefined) {
      clearTimeout(pendingClose);
      this._hoverTimers.delete(closeKey);
    }
    const openTimer = setTimeout(() => {
      if (isTopLevel) {
        const btn = itemEl.querySelector<HTMLElement>('.uwc-mbar__item-btn');
        if (btn) {
          const r = btn.getBoundingClientRect();
          this._panelCoords.set(path, { top: r.bottom + 4, left: r.left });
          this._panelTriggers.set(path, btn);
        }
      } else {
        // nested <li> — position submenu to the right of this item
        const r = itemEl.getBoundingClientRect();
        this._panelCoords.set(path, { top: r.top, left: r.right + 4 });
        this._panelTriggers.set(path, itemEl);
      }
      this._openPath(path);
    }, 80);
    this._hoverTimers.set(path, openTimer);
  }

  private _onItemMouseLeave(path: string): void {
    const pendingOpen = this._hoverTimers.get(path);
    if (pendingOpen !== undefined) {
      clearTimeout(pendingOpen);
      this._hoverTimers.delete(path);
    }
    const closeTimer = setTimeout(() => {
      this._closePath(path);
    }, 150);
    this._hoverTimers.set(path + '-close', closeTimer);
  }

  // ── Item execution ────────────────────────────────────────────────

  private _executeItem(item: MenuItem, e: Event): void {
    if (item.disabled) return;
    const ev: MenuItemCommandEvent = { originalEvent: e, item };
    item.command?.(ev);
    emit<MenuItemSelectEventDetail>(this, 'uwc-item-select', { item, originalEvent: e });
  }

  // ── Top-level keyboard navigation ─────────────────────────────────

  private _topLevelFocusable(): { item: MenuItem; index: number }[] {
    return this.items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.visible !== false && !item.separator);
  }

  private _focusTopBtn(index: number): void {
    this._focusedTopIndex = index;
    requestAnimationFrame(() => {
      const btn = this.shadowRoot?.querySelector<HTMLElement>(
        `.uwc-mbar__list [data-top-index="${index}"]`
      );
      btn?.focus();
    });
  }

  private _handleTopKeydown(e: KeyboardEvent, item: MenuItem, index: number): void {
    const focusable = this._topLevelFocusable();
    const pos = focusable.findIndex(f => f.index === index);

    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const next = focusable[(pos + 1) % focusable.length];
        if (next) {
          this._closePath(String(index));
          this._focusTopBtn(next.index);
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const prev = focusable[(pos - 1 + focusable.length) % focusable.length];
        if (prev) {
          this._closePath(String(index));
          this._focusTopBtn(prev.index);
        }
        break;
      }
      case 'ArrowDown':
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (item.items?.length) {
          const btn = e.currentTarget as HTMLElement;
          const r = btn.getBoundingClientRect();
          this._panelCoords.set(String(index), { top: r.bottom + 4, left: r.left });
          this._panelTriggers.set(String(index), btn);
          this._openPath(String(index));
          // Focus first item in submenu
          requestAnimationFrame(() => {
            const first = this.shadowRoot?.querySelector<HTMLElement>(
              `.uwc-mbar__panel[data-panel-path="${index}"] [data-panel-item-path]`
            );
            first?.focus();
          });
        } else {
          this._selectedTopIndex = index;
          this._selectedPath = String(index);
          this._executeItem(item, e);
          this._closeAll();
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        this._closePath(String(index));
        break;
      }
      case 'Tab': {
        this._closeAll();
        break;
      }
    }
  }

  // ── Panel item keyboard navigation ────────────────────────────────

  private _handlePanelKeydown(
    e: KeyboardEvent,
    item: MenuItem,
    path: string,
    parentPath: string,
    siblingPaths: string[],
  ): void {
    const pos = siblingPaths.indexOf(path);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextPath = siblingPaths[(pos + 1) % siblingPaths.length];
        if (nextPath) this._focusPanelItem(nextPath);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevPath = siblingPaths[(pos - 1 + siblingPaths.length) % siblingPaths.length];
        if (prevPath) this._focusPanelItem(prevPath);
        break;
      }
      case 'Home': {
        e.preventDefault();
        if (siblingPaths[0]) this._focusPanelItem(siblingPaths[0]);
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = siblingPaths.at(-1);
        if (last) this._focusPanelItem(last);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        if (item.items?.length) {
          const li = (e.currentTarget as HTMLElement).closest('li') as HTMLElement | null;
          if (li) {
            const r = li.getBoundingClientRect();
            this._panelCoords.set(path, { top: r.top, left: r.right + 4 });
            this._panelTriggers.set(path, li);
          }
          this._openPath(path);
          requestAnimationFrame(() => {
            const first = this.shadowRoot?.querySelector<HTMLElement>(
              `.uwc-mbar__panel[data-panel-path="${path}"] [data-panel-item-path]`
            );
            first?.focus();
          });
        }
        break;
      }
      case 'ArrowLeft':
      case 'Escape': {
        e.preventDefault();
        this._closePath(path);
        // Return focus to parent item
        requestAnimationFrame(() => {
          if (parentPath === '') {
            // Return to top-level button
            const topIdx = parseInt(path.split('.')[0], 10);
            const btn = this.shadowRoot?.querySelector<HTMLElement>(
              `[data-top-index="${topIdx}"]`
            );
            btn?.focus();
          } else {
            this._focusPanelItem(parentPath);
          }
        });
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (item.items?.length) {
          this._openPath(path);
          requestAnimationFrame(() => {
            const first = this.shadowRoot?.querySelector<HTMLElement>(
              `.uwc-mbar__panel[data-panel-path="${path}"] [data-panel-item-path]`
            );
            first?.focus();
          });
        } else {
          this._selectedTopIndex = parseInt(path.split('.')[0], 10);
          this._selectedPath = path;
          this._executeItem(item, e);
          this._closeAll();
        }
        break;
      }
      case 'Tab': {
        this._closeAll();
        break;
      }
    }
  }

  private _focusPanelItem(path: string): void {
    requestAnimationFrame(() => {
      const el = this.shadowRoot?.querySelector<HTMLElement>(
        `[data-panel-item-path="${path}"]`
      );
      el?.focus();
    });
  }

  // ── Render helpers ────────────────────────────────────────────────

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has('_openPaths') || changed.has('_mobileOpen')) {
      this._syncPopovers();
    }
  }

  private _syncPopovers(): void {
    // ── Desktop panels (top-level + nested) ─────────────────────────
    const panels: NodeListOf<HTMLElement> | HTMLElement[] = this.shadowRoot?.querySelectorAll<HTMLElement>('[data-panel-popover]') ?? [];
    for (const panel of panels) {
      const path = panel.dataset.panelPopover!;
      if (this._openPaths.has(path)) {
        const coords = this._panelCoords.get(path);
        // Write a best-guess position before showPopover so the panel doesn't
        // flash at 0,0 before the rAF clamp fires
        if (coords) {
          panel.style.top  = `${coords.top}px`;
          panel.style.left = `${coords.left}px`;
        }
        try { panel.showPopover(); } catch { /* already open */ }
        // After showPopover the browser promotes the element to the top layer
        // and performs layout. One rAF gives us real getBoundingClientRect()
        // dimensions so we can clamp/flip within the viewport.
        // Mirror PlacementController FIX 2: one rAF gives real panel dimensions
        // in the top layer before we measure and clamp.
        requestAnimationFrame(() => {
          const pRect     = panel.getBoundingClientRect();
          if (pRect.width === 0) return; // not yet laid out
          const isNested  = path.includes('.');
          const triggerEl = this._panelTriggers.get(path);
          if (triggerEl) {
            const tRect     = triggerEl.getBoundingClientRect();
            const placement = isNested
              ? ((window.innerWidth - tRect.right - 4) >= pRect.width ? 'right-start' : 'left-start')
              : 'bottom-start';
            const raw    = computeCoords(tRect, pRect, placement, 4);
            const clamp  = clampToViewport(raw, pRect);
            panel.style.top  = `${clamp.top}px`;
            panel.style.left = `${clamp.left}px`;
          } else if (coords) {
            const clamp = clampToViewport(coords, pRect);
            panel.style.top  = `${clamp.top}px`;
            panel.style.left = `${clamp.left}px`;
          }
        });
      } else {
        try { panel.hidePopover(); } catch { /* already hidden */ }
      }
    }

    // ── Mobile panel ────────────────────────────────────────────────
    const mobilePanel = this.shadowRoot?.querySelector<HTMLElement>('#uwc-mbar-mobile-panel');
    if (!mobilePanel) return;
    if (this._mobileOpen) {
      this._positionMobilePanel(mobilePanel);
      try { mobilePanel.showPopover(); } catch { /* already open */ }
    } else {
      try { mobilePanel.hidePopover(); } catch { /* already hidden */ }
    }
  }

  /** Render a top-level item in the horizontal desktop list. */
  private _renderTopItem(item: MenuItem, index: number): TemplateResult {
    if (item.visible === false) return html``;
    if (item.separator) {
      return html`
        <li role="none" class="uwc-mbar__item">
          <div class="uwc-mbar__separator" role="separator" aria-hidden="true"></div>
        </li>`;
    }

    const path = String(index);
    const hasChildren = Boolean(item.items?.length);
    const isOpen = this._isOpen(path);

    return html`
      <li
        role="none"
        class=${classMap({
          'uwc-mbar__item':          true,
          'uwc-mbar__item--open':    isOpen,
          'uwc-mbar__item--selected': this._selectedTopIndex === index,
        })}
        @mouseenter=${(e: MouseEvent) => hasChildren ? this._onItemMouseEnter(path, e.currentTarget as HTMLElement) : undefined}
        @mouseleave=${() => hasChildren ? this._onItemMouseLeave(path) : undefined}
      >
        ${item.url && !hasChildren && !item.disabled
          ? html`
            <a
              href=${item.url}
              target=${item.target ?? '_self'}
              title=${item.title ?? ''}
              role="menuitem"
              class="uwc-mbar__item-btn"
              data-top-index=${index}
              @click=${(e: MouseEvent) => {
                e.stopPropagation();
                this._selectedTopIndex = index;
                this._selectedPath = String(index);
                this._executeItem(item, e);
                this._closeAll();
              }}
              @keydown=${(e: KeyboardEvent) => this._handleTopKeydown(e, item, index)}
              @focus=${() => { this._focusedTopIndex = index; }}
            >
              ${item.icon
                ? html`<span class="uwc-mbar__item-icon ${item.iconClass ?? ''}" aria-hidden="true">
                    <uwc-icon name=${item.icon} size="1.15em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
                  </span>`
                : nothing}
              <span>${item.label ?? ''}</span>
            </a>`
          : html`
            <button
              type="button"
              role="menuitem"
              class="uwc-mbar__item-btn"
              data-top-index=${index}
              aria-haspopup=${hasChildren ? 'menu' : 'false'}
              aria-expanded=${hasChildren ? String(isOpen) : 'false'}
              ?disabled=${item.disabled}
              title=${item.title ?? ''}
              @click=${(e: MouseEvent) => {
                e.stopPropagation();
                if (item.disabled) return;
                if (hasChildren) {
                  if (isOpen) {
                    this._closePath(path);
                  } else {
                    const btn = e.currentTarget as HTMLElement;
                    const r = btn.getBoundingClientRect();
                    this._panelCoords.set(path, { top: r.bottom + 4, left: r.left });
                    this._panelTriggers.set(path, btn);
                    this._openPath(path);
                  }
                } else {
                  this._selectedTopIndex = index;
                  this._selectedPath = String(index);
                  this._executeItem(item, e);
                  this._closeAll();
                }
              }}
              @keydown=${(e: KeyboardEvent) => this._handleTopKeydown(e, item, index)}
              @focus=${() => { this._focusedTopIndex = index; }}
            >
              ${item.icon
                ? html`<span class="uwc-mbar__item-icon ${item.iconClass ?? ''}" aria-hidden="true">
                    <uwc-icon name=${item.icon} size="1.15em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
                  </span>`
                : nothing}
              <span>${item.label ?? ''}</span>
              ${hasChildren ? html`<uwc-icon name="chevron-down" size="0.85em" class="uwc-mbar__chevron" aria-hidden="true"></uwc-icon>` : nothing}
            </button>`}

        ${hasChildren ? html`
          <ul
            class="uwc-mbar__panel"
            role="menu"
            popover="manual"
            data-panel-popover=${path}
            data-panel-path=${path}
            aria-label=${item.label ?? ''}
          >
            ${this._renderPanelItems(item.items ?? [], path, path)}
          </ul>` : nothing}
      </li>`;
  }

  /**
   * Recursively render panel items (both top-level dropdown and nested submenus).
   * @param items      - items to render
   * @param basePath   - dot-path prefix (e.g. "1" or "1.2")
   * @param parentPath - parent item's path (used for keyboard Escape handling)
   * @param nested     - whether this is a nested submenu (for CSS class)
   */
  private _renderPanelItems(
    items: MenuItem[],
    basePath: string,
    parentPath: string,
    nested = false,
  ): TemplateResult {
    const visibleItems = items.filter(it => it.visible !== false);

    // Build sibling paths for keyboard navigation (excluding separators)
    const siblingPaths = visibleItems
      .map((it, i) => ({ it, path: `${basePath}.${i}` }))
      .filter(({ it }) => !it.separator && !it.disabled)
      .map(({ path }) => path);

    return html`
      ${repeat(
        visibleItems,
        (item) => item.id ?? item.label ?? item.separator,
        (item, i) => this._renderPanelItem(
          item,
          `${basePath}.${i}`,
          parentPath,
          siblingPaths,
          nested,
        ),
      )}`;
  }

  /** Render a single panel item (possibly with a nested submenu). */
  private _renderPanelItem(
    item: MenuItem,
    path: string,
    parentPath: string,
    siblingPaths: string[],
    nested = false,
  ): TemplateResult {
    if (item.separator) {
      return html`
        <li class="uwc-mbar__separator" role="separator" aria-hidden="true"></li>`;
    }

    const hasChildren = Boolean(item.items?.length);
    const isOpen = this._isOpen(path);

    const innerContent = html`
      ${item.icon
        ? html`<span class="uwc-mbar__panel-icon ${item.iconClass ?? ''}" aria-hidden="true">
            <uwc-icon name=${item.icon} size="1.05em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
          </span>`
        : nothing}
      <span class="uwc-mbar__panel-label">${item.label ?? ''}</span>
      ${item.badge
        ? html`<span class="uwc-mbar__panel-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
        : nothing}
      ${hasChildren ? html`<uwc-icon name="chevron-right" size="0.85em" class="uwc-mbar__chevron" aria-hidden="true"></uwc-icon>` : nothing}
    `;

    return html`
      <li
        role="none"
        class=${classMap({
          'uwc-mbar__panel-li':           true,
          'uwc-mbar__panel-li--open':     isOpen,
          'uwc-mbar__panel-li--disabled': !!item.disabled,
          'uwc-mbar__panel-li--danger':   !!item.danger,
          'uwc-mbar__panel-li--active':   this._selectedPath === path,
        })}
        style=${item.style ? styleMap(item.style as Record<string, string>) : ''}
        @mouseenter=${(e: MouseEvent) => hasChildren ? this._onItemMouseEnter(path, e.currentTarget as HTMLElement) : undefined}
        @mouseleave=${() => hasChildren ? this._onItemMouseLeave(path) : undefined}
      >
        ${item.url && !item.disabled
          ? html`
            <a
              href=${item.url}
              target=${item.target ?? '_self'}
              title=${item.title ?? ''}
              role="menuitem"
              class="uwc-mbar__panel-item"
              data-panel-item-path=${path}
              tabindex="-1"
              @click=${(e: MouseEvent) => {
                e.stopPropagation();
                this._selectedTopIndex = parseInt(path.split('.')[0], 10);
                this._selectedPath = path;
                this._executeItem(item, e);
                this._closeAll();
              }}
              @keydown=${(e: KeyboardEvent) =>
                this._handlePanelKeydown(e, item, path, parentPath, siblingPaths)}
            >
              ${innerContent}
            </a>`
          : html`
            <button
              type="button"
              role="menuitem"
              class="uwc-mbar__panel-item"
              data-panel-item-path=${path}
              tabindex="-1"
              ?disabled=${item.disabled}
              title=${item.title ?? ''}
              aria-haspopup=${hasChildren ? 'menu' : 'false'}
              aria-expanded=${hasChildren ? String(isOpen) : 'false'}
              @click=${(e: MouseEvent) => {
                e.stopPropagation();
                if (item.disabled) return;
                if (hasChildren) {
                  const li = (e.currentTarget as HTMLElement).closest('li') as HTMLElement;
                  if (!isOpen && li) {
                    const r = li.getBoundingClientRect();
                    this._panelCoords.set(path, { top: r.top, left: r.right + 4 });
                    this._panelTriggers.set(path, li);
                  }
                  isOpen ? this._closePath(path) : this._openPath(path);
                } else {
                  this._selectedTopIndex = parseInt(path.split('.')[0], 10);
                  this._selectedPath = path;
                  this._executeItem(item, e);
                  this._closeAll();
                }
              }}
              @keydown=${(e: KeyboardEvent) =>
                this._handlePanelKeydown(e, item, path, parentPath, siblingPaths)}
            >
              ${innerContent}
            </button>`}

        ${hasChildren && isOpen
          ? html`
            <ul
              class="uwc-mbar__panel uwc-mbar__panel--nested"
              role="menu"
              popover="manual"
              data-panel-popover=${path}
              data-panel-path=${path}
              aria-label=${item.label ?? ''}
            >
              ${this._renderPanelItems(item.items ?? [], path, path, true)}
            </ul>`
          : nothing}
      </li>`;
  }

  /**
   * Render all top-level items vertically for the mobile panel.
   * Submenus are inline (no absolute positioning) and toggle on click.
   */
  private _renderMobileItems(items: MenuItem[], depth = 0, rootIdx?: number): TemplateResult {
    return html`
      ${items
        .filter(it => it.visible !== false)
        .map((item, i) => {
          const myRootIdx = depth === 0 ? i : (rootIdx ?? i);
          if (item.separator) {
            return html`
              <li class="uwc-mbar__separator" role="separator" aria-hidden="true"></li>`;
          }

          // Use a unique mobile path key to track expansion independently of desktop
          const mobilePath = `mobile-${depth}-${i}`;
          const hasChildren = Boolean(item.items?.length);
          const isOpen = this._isOpen(mobilePath);

          return html`
            <li
              role="none"
              class=${classMap({
                'uwc-mbar__panel-li':       true,
                'uwc-mbar__panel-li--open': isOpen,
              })}
            >
              ${item.url && !hasChildren && !item.disabled
                ? html`
                  <a
                    href=${item.url}
                    target=${item.target ?? '_self'}
                    role="menuitem"
                    class="uwc-mbar__panel-item"
                    @click=${(e: MouseEvent) => {
                      e.stopPropagation();
                      this._selectedTopIndex = myRootIdx;
                      this._selectedPath = null;
                      this._executeItem(item, e);
                      this._closeAll();
                    }}
                  >
                    ${item.icon
                      ? html`<span class="uwc-mbar__panel-icon ${item.iconClass ?? ''}" aria-hidden="true">
                          <uwc-icon name=${item.icon} size="1.05em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
                        </span>`
                      : nothing}
                    <span class="uwc-mbar__panel-label">${item.label ?? ''}</span>
                    ${item.badge
                      ? html`<span class="uwc-mbar__panel-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
                      : nothing}
                  </a>`
                : html`
                  <button
                    type="button"
                    role="menuitem"
                    class="uwc-mbar__panel-item"
                    ?disabled=${item.disabled}
                    aria-haspopup=${hasChildren ? 'menu' : 'false'}
                    aria-expanded=${hasChildren ? String(isOpen) : 'false'}
                    @click=${(e: MouseEvent) => {
                      e.stopPropagation();
                      if (item.disabled) return;
                      if (hasChildren) {
                        isOpen ? this._closePath(mobilePath) : this._openPath(mobilePath);
                      } else {
                        this._selectedTopIndex = myRootIdx;
                        this._selectedPath = null;
                        this._executeItem(item, e);
                        this._closeAll();
                      }
                    }}
                  >
                    ${item.icon
                      ? html`<span class="uwc-mbar__panel-icon ${item.iconClass ?? ''}" aria-hidden="true">
                          <uwc-icon name=${item.icon} size="1.05em" library=${item.iconLibrary ?? 'default'}></uwc-icon>
                        </span>`
                      : nothing}
                    <span class="uwc-mbar__panel-label">${item.label ?? ''}</span>
                    ${item.badge
                      ? html`<span class="uwc-mbar__panel-badge ${item.badgeClass ?? ''}">${item.badge}</span>`
                      : nothing}
                    ${hasChildren ? html`<uwc-icon name="chevron-down" size="0.85em" class="uwc-mbar__chevron" aria-hidden="true"></uwc-icon>` : nothing}
                  </button>`}

              ${hasChildren && isOpen
                ? html`
                  <ul
                    class="uwc-mbar__panel uwc-mbar__panel--nested"
                    role="menu"
                    aria-label=${item.label ?? ''}
                  >
                    ${this._renderMobileItems(item.items ?? [], depth + 1, myRootIdx)}
                  </ul>`
                : nothing}
            </li>`;
        })}`;
  }

  // ── Render ────────────────────────────────────────────────────────

  protected override render(): TemplateResult {
    const visibleItems = this.items.filter(it => it.visible !== false);

    return html`
      <nav
        class="uwc-mbar"
        aria-label=${this.ariaLabel}
        @click=${(e: MouseEvent) => e.stopPropagation()}
      >
        <!-- Leading slot (logo / brand) -->
        <div class="uwc-mbar__start">
          <slot name="start"></slot>
        </div>

        <!-- Desktop horizontal nav list -->
        <ul
          class="uwc-mbar__list"
          role="menubar"
          aria-label=${this.ariaLabel}
        >
          ${repeat(
            visibleItems,
            (item) => item.id ?? item.label ?? item.separator,
            (item, i) => this._renderTopItem(item, i),
          )}
        </ul>

        <!-- Trailing slot (search, avatar, etc.) -->
        <div class="uwc-mbar__end">
          <slot name="end"></slot>
        </div>

        <!-- Mobile hamburger toggle -->
        <button
          type="button"
          class="uwc-mbar__hamburger"
          aria-label=${this._mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded=${String(this._mobileOpen)}
          aria-controls="uwc-mbar-mobile-panel"
          @click=${(e: MouseEvent) => {
            e.stopPropagation();
            this._mobileOpen = !this._mobileOpen;
            if (!this._mobileOpen) {
              // Collapse all mobile submenus on close
              const next = new Set(this._openPaths);
              for (const p of next) {
                if (p.startsWith('mobile-')) next.delete(p);
              }
              this._openPaths = next;
            }
          }}
        >
          <uwc-icon name=${this._mobileOpen ? 'x-lg' : 'list'} size="1.4em" aria-hidden="true"></uwc-icon>
        </button>
      </nav>

      <!-- Mobile panel — always in DOM, visibility via Popover API top-layer -->
      <div
        id="uwc-mbar-mobile-panel"
        class="uwc-mbar__mobile-panel"
        role="menu"
        popover="manual"
        aria-label=${this.ariaLabel}
      >
        ${this._mobileOpen ? html`
          <ul class="uwc-mbar__mobile-list" role="presentation">
            ${this._renderMobileItems(this.items.filter(it => it.visible !== false))}
          </ul>` : nothing}
      </div>
    `;
  }

  static override styles = [styles];
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-menubar': UwcMenubar; }
}
